/**
 * ProvisionProgress - Real-time provisioning status display
 */

import { useState, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, Loader2, Clock } from 'lucide-react';

interface ProvisionTask {
  id: string;
  phase: string;
  status: string;
  description: string;
  duration?: number;
  error?: string;
}

interface ProvisionJob {
  id: string;
  status: string;
  progress: number;
  phase?: string;
  tasksCompleted?: number;
  tasksTotal?: number;
  error_message?: string;
  generated_content?: Record<string, unknown>;
}

interface ProvisionProgressProps {
  jobId: string;
  storeName: string;
  onComplete: () => void;
  onClose: () => void;
}

export function ProvisionProgress({ jobId, storeName, onComplete, onClose }: ProvisionProgressProps) {
  const [job, setJob] = useState<ProvisionJob | null>(null);
  const [tasks, setTasks] = useState<ProvisionTask[]>([]);
  const [currentTask, setCurrentTask] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    // Start timer
    const timer = setInterval(() => {
      setElapsedTime(Date.now() - startTimeRef.current);
    }, 1000);

    // Initial fetch
    fetchStatus();

    // WebSocket for real-time updates
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws/dropgen`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'provision_status' && data.jobId === jobId) {
          handleStatusUpdate(data);
        }
      } catch (err) {
        console.warn('WebSocket parse error:', err);
      }
    };

    ws.onerror = (err) => {
      console.warn('WebSocket error, falling back to polling');
    };

    // Fallback polling
    const pollInterval = setInterval(fetchStatus, 3000);

    return () => {
      clearInterval(timer);
      clearInterval(pollInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [jobId]);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`/api/autostore/provision/job/${jobId}`);
      if (!res.ok) {
        throw new Error('Job not found');
      }
      const data = await res.json();

      if (data.liveStatus) {
        setJob({
          id: jobId,
          status: 'running',
          progress: data.liveStatus.progress || 0,
          phase: data.liveStatus.phase,
          tasksCompleted: data.liveStatus.completed,
          tasksTotal: data.liveStatus.total
        });

        // Update tasks from live status
        if (data.liveStatus.tasks) {
          setTasks(data.liveStatus.tasks);
        }
      } else if (data.job) {
        setJob(data.job);
        if (data.job.tasks) {
          setTasks(data.job.tasks);
        }

        // Check if job is complete
        if (data.job.status === 'completed') {
          setTimeout(onComplete, 1500);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
    }
  };

  const handleStatusUpdate = (data: Record<string, unknown>) => {
    if (data.event === 'task:start') {
      setCurrentTask(data.description as string);
      setTasks(prev => {
        const existing = prev.find(t => t.id === data.taskId);
        if (existing) {
          return prev.map(t => t.id === data.taskId ? { ...t, status: 'running' } : t);
        }
        return [...prev, {
          id: data.taskId as string,
          phase: data.phase as string,
          status: 'running',
          description: data.description as string
        }];
      });
    }

    if (data.event === 'task:complete') {
      setTasks(prev => prev.map(t =>
        t.id === data.taskId
          ? { ...t, status: 'completed', duration: data.duration as number }
          : t
      ));
      setCurrentTask('');

      // Update progress
      if (typeof data.progress === 'number') {
        setJob(prev => prev ? { ...prev, progress: data.progress as number } : prev);
      }
    }

    if (data.event === 'task:error') {
      setTasks(prev => prev.map(t =>
        t.id === data.taskId
          ? { ...t, status: 'failed', error: data.error as string }
          : t
      ));
      setCurrentTask('');
    }

    if (data.event === 'phase:start') {
      setJob(prev => prev ? { ...prev, phase: data.phase as string } : prev);
    }

    if (data.event === 'job:complete') {
      setJob(prev => prev ? { ...prev, status: 'completed', progress: 100 } : prev);
      setTimeout(onComplete, 1500);
    }

    if (data.event === 'job:error') {
      setJob(prev => prev ? { ...prev, status: 'failed', error_message: data.error as string } : prev);
      setError(data.error as string);
    }
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const phases = ['generate', 'configure', 'validate'];
  const currentPhaseIndex = phases.indexOf(job?.phase || 'generate');

  const getTasksByPhase = (phase: string) => tasks.filter(t => t.phase === phase);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold">Provisioning {storeName}</h2>
            <p className="text-sm text-zinc-400">Job ID: {jobId}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-zinc-400">
              <Clock className="w-4 h-4" />
              {formatTime(elapsedTime)}
            </div>
            <span className="text-sm font-medium">
              {job?.progress || 0}%
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-zinc-700 rounded-full mb-4 overflow-hidden">
          <div
            className="h-full bg-purple-500 transition-all duration-500 ease-out"
            style={{ width: `${job?.progress || 0}%` }}
          />
        </div>

        {/* Phase indicators */}
        <div className="flex justify-between mb-6">
          {phases.map((phase, i) => (
            <div
              key={phase}
              className={`flex items-center gap-2 ${
                i <= currentPhaseIndex ? 'text-purple-400' : 'text-zinc-500'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                i < currentPhaseIndex ? 'bg-purple-600' :
                i === currentPhaseIndex ? 'bg-purple-600 animate-pulse' :
                'bg-zinc-700'
              }`}>
                {i < currentPhaseIndex ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  i + 1
                )}
              </div>
              <span className="capitalize hidden sm:inline">{phase}</span>
            </div>
          ))}
        </div>

        {/* Current task */}
        {currentTask && (
          <div className="p-3 bg-zinc-700/50 rounded-lg mb-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
            <span className="text-sm">{currentTask}</span>
          </div>
        )}

        {/* Error display */}
        {(error || job?.error_message) && (
          <div className="p-3 mb-4 bg-red-900/50 rounded text-red-200 text-sm">
            {error || job?.error_message}
          </div>
        )}

        {/* Task list by phase */}
        <div className="flex-1 overflow-auto space-y-4">
          {phases.map(phase => {
            const phaseTasks = getTasksByPhase(phase);
            if (phaseTasks.length === 0) return null;

            return (
              <div key={phase}>
                <h4 className="text-xs uppercase text-zinc-500 mb-2 font-medium">{phase}</h4>
                <div className="space-y-1">
                  {phaseTasks.map((task) => (
                    <div
                      key={task.id}
                      className={`flex items-center justify-between p-2 rounded text-sm ${
                        task.status === 'completed' ? 'text-green-400 bg-green-500/10' :
                        task.status === 'failed' ? 'text-red-400 bg-red-500/10' :
                        task.status === 'running' ? 'text-purple-400 bg-purple-500/10' :
                        'text-zinc-400'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {task.status === 'completed' && <CheckCircle className="w-4 h-4" />}
                        {task.status === 'failed' && <XCircle className="w-4 h-4" />}
                        {task.status === 'running' && <Loader2 className="w-4 h-4 animate-spin" />}
                        {task.status === 'pending' && <div className="w-4 h-4 rounded-full border border-zinc-600" />}
                        <span>{task.description || task.id.replace(/_/g, ' ')}</span>
                      </div>
                      {task.duration && (
                        <span className="text-xs text-zinc-500">
                          {(task.duration / 1000).toFixed(1)}s
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Completion or close button */}
        <div className="mt-4 pt-4 border-t border-zinc-700 flex justify-end">
          {job?.status === 'completed' ? (
            <button
              onClick={onComplete}
              className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded font-medium"
            >
              Done
            </button>
          ) : job?.status === 'failed' ? (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-zinc-600 hover:bg-zinc-500 rounded"
            >
              Close
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded text-sm"
            >
              Run in Background
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProvisionProgress;
